import { useState, useEffect } from 'react';
import { FiArrowLeft, FiMail, FiMapPin, FiPhone, FiUser, FiUsers, FiCalendar, FiSettings, FiDownload, FiLock, FiEye, FiEyeOff, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fetchUserById, updateUser, fetchAreaHeads, fetchVisits, changePassword } from '../api/api';
import './UserDetails.css';

const UserDetails = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [user, setUser] = useState(null);
  const [areaHead, setAreaHead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deactivating, setDeactivating] = useState(false);
  const [visits, setVisits] = useState([]);
  const [visitsLoading, setVisitsLoading] = useState(false);

  // Change Password Modal State
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    setLoading(true);

    const loadUserAndAreaHead = async () => {
      try {
        const userData = await fetchUserById(userId);
        if (!cancelled) {
          setUser(userData);

          // If user has an area head assigned, fetch area head details
          const areaHeadId = userData?.areaHeadId || userData?.area_head_id;
          if (areaHeadId) {
            try {
              const areaHeads = await fetchAreaHeads();
              const matchedAreaHead = areaHeads.find(ah => String(ah.id) === String(areaHeadId));
              setAreaHead(matchedAreaHead || null);
            } catch (err) {
              console.error('Failed to load area head:', err);
            }
          }

          // Load visits for AMC users
          if (userData?.site_type === 'AMC' || userData?.siteType === 'AMC') {
            loadVisits(userId);
          }
        }
      } catch (err) {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadUserAndAreaHead();
    return () => { cancelled = true; };
  }, [userId]);

  const loadVisits = async (uid) => {
    setVisitsLoading(true);
    try {
      const visitsData = await fetchVisits({ userId: uid, limit: 3 });
      setVisits(Array.isArray(visitsData) ? visitsData.slice(0, 3) : []);
    } catch (err) {
      console.error('Failed to load visits:', err);
      setVisits([]);
    } finally {
      setVisitsLoading(false);
    }
  };

  const getRoleColor = (role) => {
    if (role === 'admin') return 'var(--admin, #FF9800)';
    if (role === 'engineer') return 'var(--success, #4CAF50)';
    if (role === 'area_head') return 'var(--info, #2196F3)';
    return 'var(--primary, #2C5FFF)';
  };

  const getRoleLabel = (role) => {
    if (role === 'area_head') return 'Area Head';
    return (role || '').charAt(0).toUpperCase() + (role || '').slice(1);
  };

  const handleDeactivate = async () => {
    if (!user) return;
    if (!window.confirm(`Deactivate ${user.name || user.email}? They will no longer be able to login.`)) return;
    setDeactivating(true);
    try {
      await updateUser(user.id, { is_active: false });
      setUser((prev) => ({ ...prev, is_active: false }));
    } catch (err) {
      alert(err.message || 'Failed to deactivate user');
    } finally {
      setDeactivating(false);
    }
  };

  const handleReactivate = async () => {
    if (!user) return;
    if (!window.confirm(`Reactivate ${user.name || user.email}?`)) return;
    setDeactivating(true);
    try {
      await updateUser(user.id, { is_active: true });
      setUser((prev) => ({ ...prev, is_active: true }));
    } catch (err) {
      alert(err.message || 'Failed to reactivate user');
    } finally {
      setDeactivating(false);
    }
  };

  // Password change handlers
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
    setPasswordError('');
    setPasswordSuccess('');
  };

  const handleSubmitPasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }
    if (passwordData.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    setChangingPassword(true);
    try {
      await changePassword(passwordData.currentPassword, passwordData.newPassword);
      setPasswordSuccess('Password changed successfully!');
      setTimeout(() => {
        setShowPasswordModal(false);
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setPasswordSuccess('');
      }, 1500);
    } catch (err) {
      setPasswordError(err.message || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  // Download engineer report
  const handleDownloadReport = async () => {
    if (!user || user.role !== 'engineer') return;

    try {
      // Open report download in new window/tab
      const reportUrl = `https://nextstep.mayurr.in/api/reports/engineer/${user.id}`;
      const headers = { 'Authorization': `Bearer ${JSON.parse(localStorage.getItem('user') || '{}')?.token}` };

      const response = await fetch(reportUrl, { headers });
      if (!response.ok) throw new Error('Failed to download report');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `engineer_report_${user.user_id || user.userId}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert(err.message || 'Failed to download report');
    }
  };

  const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
  const formatDateTime = (d) => (d ? new Date(d).toLocaleString('en-IN') : '—');

  // Parse system types and quantities
  const getSystemTypes = () => {
    if (!user) return [];
    const types = user.system_types || user.systemTypes || [];
    const quantities = user.system_type_quantities || user.systemTypeQuantities || {};

    if (Array.isArray(types) && types.length > 0) {
      return types.map(type => ({
        name: type,
        quantity: quantities[type] || 1
      }));
    }
    if (user.system_type || user.systemType) {
      return [{ name: user.system_type || user.systemType, quantity: 1 }];
    }
    return [];
  };

  const isSelf = String(user?.id) === String(currentUser?.id);
  const systemTypes = getSystemTypes();

  if (loading) {
    return (
      <div className="user-details">
        <div className="user-details-header-bar">
          <button className="back-btn" onClick={() => navigate(-1)}><FiArrowLeft /> Back</button>
        </div>
        <div className="user-details-loading">
          <div className="loading-spinner"></div>
          <p>Loading user details...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="user-details">
        <div className="user-details-header-bar">
          <button className="back-btn" onClick={() => navigate(-1)}><FiArrowLeft /> Back</button>
        </div>
        <div className="not-found">User not found.</div>
      </div>
    );
  }

  return (
    <div className="user-details">
      {/* Header */}
      <div className="user-details-header-bar">
        <button className="back-btn" onClick={() => navigate(-1)}><FiArrowLeft /> Back</button>
      </div>

      <div className="user-details-profile-header">
        <div className="user-avatar">
          <span>{(user.name || '?').charAt(0).toUpperCase()}</span>
        </div>
        <div className="user-profile-info">
          <h1>{user.name || '—'}</h1>
          <span className="user-role-badge" style={{ background: getRoleColor(user.role) }}>
            {getRoleLabel(user.role)}
          </span>
        </div>
      </div>

      {/* Status Badge */}
      <div className={`user-status-badge ${user.is_active === false ? 'inactive' : 'active'}`}>
        {user.is_active === false ? (
          <><FiXCircle /> Inactive Account</>
        ) : (
          <><FiCheckCircle /> Active Account</>
        )}
      </div>
      {/* Contact Section */}
      <div className="user-details-section">
        <div className="section-header">
          <FiMail className="section-icon" />
          <h3>Contact Information</h3>
        </div>
        <div className="user-details-grid">
          <div className="user-details-card">
            <span className="card-label">Email</span>
            <span className="card-value">{user.email || '—'}</span>
          </div>
          <div className="user-details-card">
            <span className="card-label">Phone</span>
            <span className="card-value">{user.phone || '—'}</span>
          </div>
          <div className="user-details-card">
            <span className="card-label">User ID</span>
            <span className="card-value">{user.userId || user.user_id || '—'}</span>
          </div>
        </div>
      </div>
      {/* Site Section */}
      {(user.site_name || user.site_address || user.site_type || user.siteType) && (
        <div className="user-details-section">
          <div className="section-header">
            <FiMapPin className="section-icon" />
            <h3>Site Details</h3>
          </div>
          <div className="user-details-grid">
            <div className="user-details-card full-width">
              <span className="card-label">Site Name</span>
              <span className="card-value">{user.site_name || user.siteName || '—'}</span>
            </div>
            <div className="user-details-card full-width">
              <span className="card-label">Site Address</span>
              <span className="card-value">{user.site_address || user.siteAddress || '—'}</span>
            </div>
            <div className="user-details-card">
              <span className="card-label">Site Type</span>
              <span className={`card-value site-type ${(user.site_type || user.siteType)?.toLowerCase?.()}`}>
                {user.site_type || user.siteType || '—'}
              </span>
            </div>
            {(user.state || user.area) && (
              <div className="user-details-card">
                <span className="card-label">Location</span>
                <span className="card-value">
                  {[user.state, user.area].filter(Boolean).join(', ') || '—'}
                </span>
              </div>
            )}
            {(user.car_count || user.carCount) && (
              <div className="user-details-card">
                <span className="card-label">Car Count</span>
                <span className="card-value">{user.car_count || user.carCount}</span>
              </div>
            )}
            {(user.total_systems || user.totalSystems) && (
              <div className="user-details-card">
                <span className="card-label">Total Systems</span>
                <span className="card-value">{user.total_systems || user.totalSystems}</span>
              </div>
            )}
          </div>

          {/* System Types with Quantities */}
          {systemTypes.length > 0 && (
            <div className="system-types-section">
              <span className="card-label">System Types</span>
              <div className="system-types-chips">
                {systemTypes.map((type, idx) => (
                  <span key={idx} className="system-type-chip">
                    {type.name}
                    <span className="quantity-badge">x{type.quantity}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {/* Assigned Area Head Section */}
      {(user.areaHeadId || user.area_head_id) && (
        <div className="user-details-section">
          <div className="section-header">
            <FiUsers className="section-icon" />
            <h3>Assigned Area Head</h3>
          </div>
          {areaHead ? (
            <div className="area-head-card">
              <div className="area-head-avatar">
                {(areaHead.name || '?').charAt(0).toUpperCase()}
              </div>
              <div className="area-head-info">
                <span className="area-head-name">{areaHead.name}</span>
                <span className="area-head-id">ID: {areaHead.user_id || areaHead.userId}</span>
                {areaHead.email && <span className="area-head-detail"><FiMail /> {areaHead.email}</span>}
                {areaHead.phone && <span className="area-head-detail"><FiPhone /> {areaHead.phone}</span>}
                {(areaHead.state || areaHead.area) && (
                  <span className="area-head-detail"><FiMapPin /> {[areaHead.state, areaHead.area].filter(Boolean).join(', ')}</span>
                )}
              </div>
            </div>
          ) : (
            <div className="area-head-not-found">
              <span>Area Head ID: {user.areaHeadId || user.area_head_id}</span>
              <span className="not-found-note">(Details not available)</span>
            </div>
          )}
        </div>
      )}
      {/* Preventive Visits Section - AMC Users Only */}
      {(user.site_type === 'AMC' || user.siteType === 'AMC') && (
        <div className="user-details-section">
          <div className="section-header">
            <FiCalendar className="section-icon" />
            <h3>Preventive Maintenance Visits</h3>
          </div>
          {visitsLoading ? (
            <div className="visits-loading">Loading visits...</div>
          ) : visits.length === 0 ? (
            <div className="no-visits">No preventive visits recorded yet.</div>
          ) : (
            <>
              <div className="visits-list">
                {visits.map((visit, idx) => (
                  <div key={visit.id || idx} className={`visit-card ${visit.status?.toLowerCase()}`}>
                    <div className="visit-header">
                      <span className="visit-date">{formatDate(visit.visit_date || visit.scheduled_date)}</span>
                      <span className={`visit-status ${visit.status?.toLowerCase()}`}>{visit.status || 'Pending'}</span>
                    </div>
                    <div className="visit-details">
                      {visit.engineer_name && <span className="visit-engineer">Engineer: {visit.engineer_name}</span>}
                      {visit.notes && <span className="visit-notes">{visit.notes}</span>}
                    </div>
                  </div>
                ))}
              </div>
              <button className="view-all-btn" onClick={() => navigate(`/admin/visits?userId=${user.id}`)}>
                View All Visits
              </button>
            </>
          )}
        </div>
      )}

      {/* Account Section */}
      <div className="user-details-section">
        <div className="section-header">
          <FiSettings className="section-icon" />
          <h3>Account Information</h3>
        </div>
        <div className="user-details-grid">
          <div className="user-details-card">
            <span className="card-label">Account Created</span>
            <span className="card-value">{formatDateTime(user.created_at)}</span>
          </div>
          <div className="user-details-card">
            <span className="card-label">Last Updated</span>
            <span className="card-value">{formatDateTime(user.updated_at)}</span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="user-details-actions">
        {/* Download Engineer Report - Engineers Only */}
        {user.role === 'engineer' && (
          <button type="button" className="action-btn download-btn" onClick={handleDownloadReport}>
            <FiDownload /> Download Engineer Report
          </button>
        )}

        {/* Change Password Button */}
        <button type="button" className="action-btn password-btn" onClick={() => setShowPasswordModal(true)}>
          <FiLock /> Change Password
        </button>

        {/* Deactivate/Reactivate User */}
        {isSelf ? (
          <div className="self-protection-note">
            <FiXCircle /> You cannot deactivate your own account.
          </div>
        ) : user.is_active === false ? (
          <button type="button" className="action-btn reactivate-btn" onClick={handleReactivate} disabled={deactivating}>
            <FiCheckCircle /> {deactivating ? 'Updating...' : 'Reactivate User'}
          </button>
        ) : (
          <button type="button" className="action-btn deactivate-btn" onClick={handleDeactivate} disabled={deactivating}>
            <FiXCircle /> {deactivating ? 'Updating...' : 'Deactivate User'}
          </button>
        )}
      </div>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="modal-overlay" onClick={() => !changingPassword && setShowPasswordModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><FiLock /> Change Password</h2>
              <button className="modal-close" onClick={() => !changingPassword && setShowPasswordModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmitPasswordChange} className="password-form">
              {passwordError && <div className="error-message">{passwordError}</div>}
              {passwordSuccess && <div className="success-message">{passwordSuccess}</div>}

              <div className="form-group">
                <label>Current Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPasswords.current ? 'text' : 'password'}
                    name="currentPassword"
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    placeholder="Enter current password"
                    disabled={changingPassword}
                  />
                  <button type="button" className="toggle-password" onClick={() => setShowPasswords(p => ({ ...p, current: !p.current }))}>
                    {showPasswords.current ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>New Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPasswords.new ? 'text' : 'password'}
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    placeholder="Enter new password (min 6 chars)"
                    disabled={changingPassword}
                    minLength={6}
                  />
                  <button type="button" className="toggle-password" onClick={() => setShowPasswords(p => ({ ...p, new: !p.new }))}>
                    {showPasswords.new ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>Confirm New Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    placeholder="Confirm new password"
                    disabled={changingPassword}
                  />
                  <button type="button" className="toggle-password" onClick={() => setShowPasswords(p => ({ ...p, confirm: !p.confirm }))}>
                    {showPasswords.confirm ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowPasswordModal(false)} disabled={changingPassword}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit" disabled={changingPassword}>
                  {changingPassword ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDetails;
